import { useState } from "react";
import { GiTreasureMap, GiStarsStack, GiAlarmClock, GiCrystalCluster } from "react-icons/gi";
import { useCastle } from "../api/castle";
import { PackageDef, useCreateInvoice, usePackages } from "../api/payments";
import { Loading } from "./Loading";
import { ErrorView } from "./ErrorView";
import { ResourceBar } from "../components/ResourceBar";
import { ScreenBanner } from "../components/ScreenBanner";
import { formatNumber } from "../lib/format";
import { hapticImpact, hapticNotification, openInvoice, showAlert } from "../lib/telegram";

interface Props {
  embedded?: boolean;
}

// Stars shop. Each package shows what's inside, the price in stars, and
// a Buy button. Tap triggers server-side invoice creation, then opens the
// Telegram payment sheet. On 'paid' status we refresh the castle so the
// player sees their new resources/inventory immediately.
//
// `embedded` skips the banner + resource bar so the shop can be hosted
// inside MeScreen as a sub-tab without doubling them.
export function ShopScreen({ embedded = false }: Props = {}) {
  const castle = useCastle();
  const packages = usePackages();
  const createInvoice = useCreateInvoice();
  const [pendingId, setPendingId] = useState<string | null>(null);

  if (castle.isPending || packages.isPending) {
    return <Loading message="Открываю магазин..." />;
  }
  if (castle.isError || packages.isError) {
    return <ErrorView message="Не получилось загрузить магазин." />;
  }
  const data = castle.data!;
  const list = packages.data!;

  const onBuy = async (pkg: PackageDef) => {
    hapticImpact("medium");
    setPendingId(pkg.id);
    try {
      const invoice = await createInvoice.mutateAsync(pkg.id);
      const status = await openInvoice(invoice.invoiceUrl);
      if (status === "paid") {
        hapticNotification("success");
        // Server credit lands via the bot's successful_payment handler.
        // Refresh castle to reflect new resources/inventory after a short
        // grace period — bot needs a moment to call /fulfil.
        setTimeout(() => castle.refetch(), 1500);
        await showAlert(`${pkg.title} оплачен. Награды зачислены в замок.`);
      } else if (status === "cancelled") {
        hapticNotification("warning");
      } else if (status === "failed") {
        hapticNotification("error");
        await showAlert("Оплата не прошла. Попробуй ещё раз.");
      }
    } catch (err) {
      hapticNotification("error");
      await showAlert("Не получилось создать счёт. Попробуй позже.");
    } finally {
      setPendingId(null);
    }
  };

  const body = (
    <div className="flex-1 overflow-y-auto px-3 py-3">
      <div className="space-y-3">
        {list.map((pkg) => (
          <PackageCard
            key={pkg.id}
            pkg={pkg}
            pending={pendingId === pkg.id}
            onBuy={() => onBuy(pkg)}
          />
        ))}
      </div>
      <div className="mt-4 rounded-xl bg-tg-secondaryBg/60 p-3 text-xs leading-relaxed text-tg-hint">
        Оплата проходит через Telegram Stars. Содержимое сундука зачисляется
        сразу после оплаты. Возвраты — через поддержку Telegram в течение
        21 дня.
      </div>
    </div>
  );

  if (embedded) {
    return body;
  }

  return (
    <div className="flex h-full flex-col">
      <ScreenBanner kind="profile" title="Магазин" subtitle="Telegram Stars" />
      <ResourceBar resources={data.state.resources} safeStorage={data.safeStorage} />
      {body}
    </div>
  );
}

function PackageCard({
  pkg,
  pending,
  onBuy,
}: {
  pkg: PackageDef;
  pending: boolean;
  onBuy: () => void;
}) {
  const r = pkg.contents.resources;
  const inv = pkg.contents.inventoryItems ?? {};
  const speedupTotal = Object.entries(inv)
    .filter(([id]) => id.startsWith("spd-"))
    .reduce((sum, [, n]) => sum + n, 0);

  return (
    <div className="rounded-2xl bg-tg-secondaryBg p-3 shadow-md">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="truncate text-base font-semibold">{pkg.title}</h3>
            {pkg.highlight && (
              <span className="rounded-full bg-tg-accent/20 px-2 py-0.5 text-[10px] uppercase tracking-wide text-tg-accent">
                {pkg.highlight}
              </span>
            )}
          </div>
          <div className="mt-0.5 text-xs leading-tight text-tg-hint">
            {pkg.description}
          </div>
        </div>
        <button
          type="button"
          onClick={onBuy}
          disabled={pending}
          className="flex items-center gap-1 rounded-xl bg-tg-button px-3 py-2 text-sm font-semibold text-tg-buttonText transition-all active:scale-95 disabled:opacity-50"
        >
          <GiStarsStack size={16} />
          <span className="tabular-nums">{pkg.starsPrice}</span>
        </button>
      </div>

      <div className="mt-2 grid grid-cols-2 gap-1.5 text-xs">
        {r.gold > 0 && <ContentRow label="Золото" value={r.gold} />}
        {r.wood > 0 && <ContentRow label="Дерево" value={r.wood} />}
        {r.stone > 0 && <ContentRow label="Камень" value={r.stone} />}
        {r.mana > 0 && <ContentRow label="Мана" value={r.mana} />}
        {pkg.contents.soulShards > 0 && (
          <ContentRow
            label="Осколки душ"
            value={pkg.contents.soulShards}
            icon={<GiCrystalCluster size={12} />}
          />
        )}
        {speedupTotal > 0 && (
          <ContentRow
            label="Ускорители"
            value={speedupTotal}
            icon={<GiAlarmClock size={12} />}
          />
        )}
        {pkg.contents.heroXp > 0 && (
          <ContentRow
            label="XP героев"
            value={pkg.contents.heroXp}
            icon={<GiTreasureMap size={12} />}
          />
        )}
      </div>
    </div>
  );
}

function ContentRow({
  label,
  value,
  icon,
}: {
  label: string;
  value: number;
  icon?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg bg-tg-bg/40 px-2 py-1">
      <span className="flex items-center gap-1 text-tg-hint">
        {icon}
        {label}
      </span>
      <span className="tabular-nums font-semibold">{formatNumber(value)}</span>
    </div>
  );
}
